import { VILVAD2C, CATEGORIES, STATS_UPDATED_AT } from "@/lib/siteStats";
import { formatCount } from "@/lib/statsFormat";
import countries from "@/public/countries.json";

// Stats for the Vilva D2C page. Values come from the single
// public/stats.json snapshot — see lib/siteStats.ts.
//
// Sources: total_orders / total_order_value are from the D2C store DB
// (orders where payment_status = 'success'). "Active Stores" is the customer
// count of the "E-Commerce + Social" project-workflow category — i.e. clients
// whose e-commerce work we actually delivered — NOT a shopper count and not
// the whole internal CRM list. Country count is just the length of the
// countries list this site already downloads at build time; no DB involved.

export const VILVAD2C_UPDATED_AT = STATS_UPDATED_AT;

// Indian numbering for money: 1 Cr = 1,00,00,000 and 1 L = 1,00,000. Falls
// through to smaller units so a modest real figure never renders as "₹0 Cr".
// No trailing "+" on an abbreviated figure — see formatCount in
// lib/statsFormat.ts for why.
export function formatInr(n: number): string {
  if (n >= 1_00_00_000) return `₹${Math.floor(n / 1_00_00_000)} Cr`;
  if (n >= 1_00_000) return `₹${Math.floor(n / 1_00_000)} L`;
  if (n >= 1_000) return `₹${Math.floor(n / 1_000)}k`;
  return `₹${Math.floor(n)}`;
}

// See showCount() in lib/statsFormat.ts for why a zero renders as a dash. This
// section needs its own map because "₹—" and "—" differ per tile.
const PLACEHOLDER = { orders: "—", orderValue: "—", customers: "—" };

const ecomCustomers =
  CATEGORIES.find((c) => c.name === "E-Commerce + Social")?.customers ?? 0;
const countryCount = (countries as unknown[]).length;

export const COUNTRIES_COUNT = `${countryCount}`;
export const TOTAL_ORDERS =
  VILVAD2C.total_orders > 0 ? formatCount(VILVAD2C.total_orders) : PLACEHOLDER.orders;
export const TOTAL_ORDER_VALUE =
  VILVAD2C.total_order_value > 0 ? formatInr(VILVAD2C.total_order_value) : PLACEHOLDER.orderValue;
export const TOTAL_CUSTOMERS =
  ecomCustomers > 0 ? formatCount(ecomCustomers) : PLACEHOLDER.customers;

// A plain integer, no abbreviation — countryCount never reaches formatCount's
// 10,000 threshold, so this is just the rounded whole number as a string.
const formatPlainCount = (n: number) => `${Math.round(n)}`;

// Display string + the unrounded figure behind it, so <StatNumber> can reveal
// the exact number on hover. Order matches the on-page grid.
export const VILVAD2C_STATS = [
  {
    value: COUNTRIES_COUNT,
    exact: countryCount,
    target: countryCount,
    format: formatPlainCount,
    label: "Countries Delivered To",
  },
  {
    value: TOTAL_ORDERS,
    exact: VILVAD2C.total_orders,
    target: VILVAD2C.total_orders,
    format: formatCount,
    label: "Orders Fulfilled",
  },
  {
    value: TOTAL_ORDER_VALUE,
    exact: Math.round(VILVAD2C.total_order_value),
    target: VILVAD2C.total_order_value,
    format: formatInr,
    prefix: "₹",
    label: "GMV Processed",
  },
  {
    value: TOTAL_CUSTOMERS,
    exact: ecomCustomers,
    target: ecomCustomers,
    format: formatCount,
    label: "Active Stores",
  },
];
